import React, { createContext, useCallback, useState, FC } from 'react';

export interface ConfirmDialogContent {
  title?: string;
  prompt?: string;
  message?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}

/**
 * Use to get the state of, and take actions on ConfirmDialog within ChartInteractiveView
 */
export const ChartInteractionConfirmDialogContext = createContext<{
  showDialog: (content: ConfirmDialogContent) => void;
  hideDialog: () => void;
  isOpen?: boolean;
  content: ConfirmDialogContent;
}>({
  showDialog: () => {},
  isOpen: false,
  content: {},
  hideDialog: () => {},
});

/**
 * Supports a shared ConfirmDialog component within ChartInteractiveView
 */
export const ChartInteractionConfirmDialogContextProvider: FC = ({ children }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [content, setContent] = useState<ConfirmDialogContent>({});

  const showDialog = useCallback(({ title, message, prompt, onConfirm, onCancel }) => {
    setContent({
      title,
      message,
      prompt,
      onConfirm,
      onCancel,
    });
    setIsOpen(true);
  }, []);

  const hideDialog = useCallback(() => {
    setIsOpen(false);
    setContent({
      title: '',
      message: '',
      prompt: '',
      onConfirm: undefined,
      onCancel: undefined,
    });
  }, []);

  return (
    <ChartInteractionConfirmDialogContext.Provider
      value={{
        showDialog,
        isOpen,
        content,
        hideDialog,
      }}
    >
      {children}
    </ChartInteractionConfirmDialogContext.Provider>
  );
};
